/**
 * @fileoverview SummaryStrip - A compact horizontal row of summary stat pills
 * Used above list pages (sessions, runs) to show key counts at a glance.
 */
import { cn } from '@/lib/utils'

export interface SummaryPill {
  label: string
  value: number | string
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error'
}

export interface SummaryStripProps {
  pills: SummaryPill[]
  isLoading?: boolean
  className?: string
}

const dotColors: Record<string, string> = {
  default: 'bg-muted-foreground',
  primary: 'bg-primary',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
}

export function SummaryStrip({ pills, isLoading, className }: SummaryStripProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-4 text-sm text-muted-foreground', className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-muted animate-pulse" />
            <span className="h-3.5 w-12 bg-muted rounded animate-pulse" />
          </span>
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground', className)}
      role="status"
      aria-label="Summary statistics"
    >
      {pills.map((pill, index) => (
        <span key={pill.label} className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 rounded-full flex-shrink-0', dotColors[pill.variant ?? 'default'])} />
          <span className="font-medium text-foreground">{pill.value}</span>
          <span>{pill.label}</span>
          {index < pills.length - 1 && (
            <span className="text-border ml-2 hidden sm:inline" aria-hidden>•</span>
          )}
        </span>
      ))}
    </div>
  )
}
